import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError, currencyFmt, nzToday } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Loader2, Upload, FileText, X, ArrowLeft, Camera } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CameraCapture } from "@/components/CameraCapture";
import { toast } from "sonner";

const MAX_ATTACHMENTS = 4;

// Client-side image compression to save storage space
async function compressImage(file) {
  if (!file.type.startsWith("image/")) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const maxDim = 1280;
    let { width, height } = bitmap;
    if (width > maxDim || height > maxDim) {
      const scale = maxDim / Math.max(width, height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
    const canvas = document.createElement("canvas");
    canvas.width = width; canvas.height = height;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.6));
    if (!blob || blob.size >= file.size) return file;
    const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg" });
  } catch {
    return file;
  }
}

const GST_RATE = 3 / 23;
const emptyItem = () => ({ description: "", category: "", amount: "", expense_date: nzToday(), gst_applicable: true, attachments: [] });

export default function NewClaim() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = ["admin", "super_admin"].includes(user.role);
  const [currencies, setCurrencies] = useState(["NZD"]);
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [categories, setCategories] = useState([]);
  const [managers, setManagers] = useState([]);
  const [approverId, setApproverId] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [cameraIdx, setCameraIdx] = useState(null);
  const [loaded, setLoaded] = useState(!isEdit);
  const [form, setForm] = useState({
    title: "", expense_type: "", currency: "NZD", project_code: "", description: "",
  });
  const [items, setItems] = useState([emptyItem()]);

  useEffect(() => {
    api.get("/meta/categories").then(({ data }) => setCategories(data.categories)).catch(() => {});
    api.get("/tenant/managers").then(({ data }) => setManagers(data.filter((m) => m.user_id !== user.user_id))).catch(() => {});
    api.get("/currencies").then(({ data }) => { if (data.length) setCurrencies(data); }).catch(() => {});
  }, [user.user_id]);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/claims/${id}`).then(({ data }) => {
      if (!["pending", "rejected"].includes(data.status) && !isManager) {
        toast.error("This claim can no longer be edited");
        navigate(`/claims/${id}`);
        return;
      }
      setApproverId(data.approver_id || "");
      setForm({
        title: data.title, expense_type: data.expense_type || "", currency: data.currency,
        project_code: data.project_code || "", description: data.description || "",
      });
      setItems(data.line_items.map((li) => ({
        description: li.description, category: li.category || "", amount: String(li.amount),
        expense_date: li.expense_date || nzToday(),
        gst_applicable: li.gst_applicable !== false, attachments: li.attachments || [],
      })));
      setLoaded(true);
    }).catch(() => { toast.error("Could not load claim"); navigate("/claims"); });
  }, [id, isEdit, navigate]);

  const lineGst = (i) => (i.gst_applicable ? (parseFloat(i.amount) || 0) * GST_RATE : 0);
  const total = items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const totalGst = items.reduce((s, i) => s + lineGst(i), 0);

  const updateItem = (idx, key, val) => setItems((prev) => prev.map((it, i) => i === idx ? { ...it, [key]: val } : it));
  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const onLineUpload = async (idx, e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    await uploadFiles(idx, files);
  };

  const uploadFiles = async (idx, files) => {
    if (!files.length) return;
    const current = items[idx].attachments.length;
    if (current + files.length > MAX_ATTACHMENTS) {
      return toast.error(`Up to ${MAX_ATTACHMENTS} attachments per line item`);
    }
    setUploadingIdx(idx);
    try {
      for (const raw of files) {
        const file = await compressImage(raw);
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setItems((prev) => prev.map((it, i) => i === idx ? { ...it, attachments: [...it.attachments, data] } : it));
      }
      toast.success("Attachment added");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploadingIdx(null); }
  };

  const onDrop = (idx, e) => {
    e.preventDefault();
    setDragIdx(null);
    const files = [...(e.dataTransfer?.files || [])].filter((f) => f.type.startsWith("image/") || f.type === "application/pdf");
    if (files.length) uploadFiles(idx, files);
    else if (e.dataTransfer?.files?.length) toast.error("Only images and PDF files are allowed");
  };

  const removeAttachment = (idx, fileId) =>
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, attachments: it.attachments.filter((a) => a.file_id !== fileId) } : it));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.expense_type) return toast.error("Select an expense type");
    if (managers.length > 0 && !approverId) return toast.error("Select an approver for this claim");
    const cleanItems = items
      .filter((i) => i.description && i.category && i.expense_date && parseFloat(i.amount) > 0)
      .map((i) => ({ description: i.description, category: i.category, amount: parseFloat(i.amount), expense_date: i.expense_date, gst_applicable: !!i.gst_applicable, attachments: i.attachments }));
    if (cleanItems.length !== items.length) return toast.error("Each line item needs a description, category, date and amount");
    if (!cleanItems.length) return toast.error("Add at least one valid line item");
    const payload = { ...form, line_items: cleanItems, approver_id: approverId || null };
    setBusy(true);
    try {
      if (isEdit) {
        const { data } = await api.put(`/claims/${id}`, payload);
        toast.success(data.status === "pending" ? "Claim resubmitted" : "Claim updated");
        navigate(`/claims/${id}`);
      } else {
        await api.post("/claims", payload);
        toast.success("Claim submitted");
        navigate("/claims");
      }
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!loaded) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-4xl space-y-6" data-testid="new-claim-page">
      <div>
        <button onClick={() => navigate(isEdit ? `/claims/${id}` : "/claims")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" />Back
        </button>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{isEdit ? "Edit Claim" : "Expense Claim"}</h1>
        <p className="text-sm text-muted-foreground mt-1">{isEdit ? "Update details. Rejected claims will be resubmitted for approval." : "Submit expenses with itemized line items and receipts."}</p>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <div className="bg-card border border-border rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2 md:col-span-2"><Label>Expense Type <span className="text-danger">*</span></Label>
            <Select value={form.expense_type} onValueChange={(v) => setForm({ ...form, expense_type: v })}>
              <SelectTrigger data-testid="claim-expense-type" className="rounded-sm">
                <SelectValue placeholder="Select expense type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personal" data-testid="expense-type-personal">Personal Expense Claim</SelectItem>
                <SelectItem value="company_credit_card" data-testid="expense-type-credit">Company Credit Card</SelectItem>
                <SelectItem value="company_debit_card" data-testid="expense-type-debit">Company Debit Card</SelectItem>
              </SelectContent>
            </Select></div>
          <div className="space-y-2 md:col-span-2"><Label>Title</Label>
            <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="claim-title" className="rounded-sm" placeholder="e.g. Client visit — New York" /></div>
          <div className="space-y-2"><Label>Currency</Label>
            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
              <SelectTrigger data-testid="claim-currency" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{currencies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-2"><Label>Project code</Label>
            <Input value={form.project_code} onChange={(e) => setForm({ ...form, project_code: e.target.value })} data-testid="claim-project" className="rounded-sm" placeholder="Optional" /></div>
          <div className="space-y-2 md:col-span-2"><Label>Approver</Label>
            <Select value={approverId} onValueChange={setApproverId}>
              <SelectTrigger data-testid="claim-approver" className="rounded-sm">
                <SelectValue placeholder={managers.length ? "Select an approver" : "No approvers available"} />
              </SelectTrigger>
              <SelectContent>
                {managers.map((m) => <SelectItem key={m.user_id} value={m.user_id} data-testid={`approver-option-${m.user_id}`}>{m.name} · {m.role === "admin" ? "Admin" : "Manager"}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">This person will receive your claim for approval.</p></div>
          <div className="space-y-2 md:col-span-2"><Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="claim-description" className="rounded-sm" placeholder="Notes for the approver" /></div>
        </div>

        <div className="bg-card border border-border rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm uppercase tracking-wider text-muted-foreground font-medium">Line Items</h3>
            <Button type="button" variant="outline" size="sm" onClick={addItem} className="rounded-sm" data-testid="add-item-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
          </div>
          <div className="space-y-4">
            {items.map((it, idx) => (
              <div key={idx} className="border border-border rounded-md p-4 bg-background/50" data-testid={`line-item-${idx}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium mt-2">Item {idx + 1}</span>
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)} disabled={items.length === 1} data-testid={`remove-item-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                  <div className="md:col-span-4 space-y-1.5">
                    <Label className="text-xs">Description</Label>
                    <Input placeholder="What was this expense?" value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} className="rounded-sm" data-testid={`item-desc-${idx}`} />
                  </div>
                  <div className="md:col-span-3 space-y-1.5">
                    <Label className="text-xs">Category</Label>
                    <Select value={it.category} onValueChange={(v) => updateItem(idx, "category", v)}>
                      <SelectTrigger className="rounded-sm" data-testid={`item-category-${idx}`}><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-3 space-y-1.5">
                    <Label className="text-xs">Expense date</Label>
                    <Input type="date" value={it.expense_date} onChange={(e) => updateItem(idx, "expense_date", e.target.value)} className="rounded-sm" data-testid={`item-date-${idx}`} />
                  </div>
                  <div className="md:col-span-2 space-y-1.5">
                    <Label className="text-xs">Amount</Label>
                    <Input type="number" step="0.01" min="0" placeholder="0.00" value={it.amount} onChange={(e) => updateItem(idx, "amount", e.target.value)} className="rounded-sm font-mono" data-testid={`item-amount-${idx}`} />
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer select-none" data-testid={`item-gst-label-${idx}`}>
                      <Checkbox checked={it.gst_applicable} onCheckedChange={(v) => updateItem(idx, "gst_applicable", !!v)} data-testid={`item-gst-${idx}`} />
                      <span className="text-sm">GST applicable</span>
                    </label>
                    <span className="text-sm text-muted-foreground">GST: <span className="font-mono text-foreground" data-testid={`item-gst-amount-${idx}`}>{currencyFmt(lineGst(it), form.currency)}</span></span>
                  </div>
                  <div
                    onDragOver={(e) => { e.preventDefault(); if (it.attachments.length < MAX_ATTACHMENTS) setDragIdx(idx); }}
                    onDragLeave={(e) => { e.preventDefault(); setDragIdx((d) => (d === idx ? null : d)); }}
                    onDrop={(e) => onDrop(idx, e)}
                    className={`flex items-center gap-2 flex-wrap rounded-md border border-dashed px-3 py-2.5 transition-colors ${dragIdx === idx ? "border-primary bg-primary/5" : "border-border"}`}
                    data-testid={`item-dropzone-${idx}`}
                  >
                    <label className={`inline-flex items-center gap-1.5 text-sm rounded-full border border-border px-3 py-1.5 cursor-pointer hover:bg-accent transition-colors ${it.attachments.length >= MAX_ATTACHMENTS ? "opacity-40 pointer-events-none" : ""}`} data-testid={`item-attach-file-${idx}`}>
                      <Upload className="h-3.5 w-3.5" /> Add file
                      <input type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={(e) => onLineUpload(idx, e)} />
                    </label>
                    <button type="button" className={`inline-flex items-center gap-1.5 text-sm rounded-full border border-border px-3 py-1.5 cursor-pointer hover:bg-accent transition-colors ${it.attachments.length >= MAX_ATTACHMENTS ? "opacity-40 pointer-events-none" : ""}`} data-testid={`item-attach-camera-${idx}`} onClick={() => setCameraIdx(idx)}>
                      <Camera className="h-3.5 w-3.5" /> Take photo
                    </button>
                    <span className="text-xs text-muted-foreground">{dragIdx === idx ? "Drop files to attach" : "or drag & drop files here"}</span>
                    {uploadingIdx === idx && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                  </div>
                </div>
                {it.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {it.attachments.map((a) => (
                      <span key={a.file_id} className="inline-flex items-center gap-1.5 bg-muted/60 rounded-sm px-2 py-1 text-xs max-w-[180px]" data-testid={`item-attachment-${idx}-${a.file_id}`}>
                        <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate">{a.filename}</span>
                        <button type="button" onClick={() => removeAttachment(idx, a.file_id)} data-testid={`remove-attachment-${idx}-${a.file_id}`}><X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" /></button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground mt-2">{it.attachments.length}/{MAX_ATTACHMENTS} attachments · images are compressed to save space</p>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-border flex justify-end gap-8">
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Total GST</p>
              <p className="font-mono text-xl font-semibold" data-testid="claim-total-gst">{currencyFmt(totalGst, form.currency)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Amount</p>
              <p className="font-mono text-2xl font-semibold" data-testid="claim-total">{currencyFmt(total, form.currency)}</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" className="rounded-sm" onClick={() => navigate("/claims")}>Cancel</Button>
          <Button type="submit" disabled={busy} className="rounded-sm" data-testid="submit-claim-btn">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{isEdit ? "Save & Resubmit" : "Submit Claim"}
          </Button>
        </div>
      </form>

      <CameraCapture
        open={cameraIdx !== null}
        onOpenChange={(o) => { if (!o) setCameraIdx(null); }}
        onCapture={(file) => { const i = cameraIdx; setCameraIdx(null); uploadFiles(i, [file]); }}
      />
    </div>
  );
}
